import { defineStore } from 'pinia'
import { useRoute } from 'vue-router'

import type {
  ContestColor,
  ContestGameReward,
  FormattedContestGameReward,
  GameContest,
  Merchant,
  MerchantColors,
  Outcomes,
} from '@/types'

import { clientApi } from '@/api/config'
import { setRootCss } from '@/utils/root'
import * as Sentry from '@sentry/vue'

export const useGameContest = defineStore('GameContest', {
  state: () => {
    const route = useRoute()
    return {
      contestReward: null as ContestGameReward | null,
      merchant: {} as Merchant,
      gameContest: {} as GameContest,
      isClickRequiredToPlay: false,
      redirectUrl: '',
      locale: route.query.locale?.toString() || 'fr',
      slug: route.params.storeSlug?.toString() || '',
    }
  },
  getters: {
    contestGameRewards(state): FormattedContestGameReward[] {
      return state.gameContest?.outcomes?.map((g) => {
        return {
          id: g.contestGameRewardId,
          value: g.name,
          bgColor: g.color,
          color: g.textColor,
          type: g.rewardTypeSlug,
        }
      })
    },
    merchantLogo(state) {
      return state.merchant?.media?.logo || ''
    },
    gameContestColor(state): ContestColor {
      return {
        backgroundColor: state?.gameContest?.backgroundColor || this.merchantColors.background,
        buttonColor: state?.gameContest?.buttonColor || this.merchantColors.button,
        buttonTextColor: state?.gameContest?.buttonTextColor || '#ffffff',
        textColor: state?.gameContest?.textColor || this.merchantColors.text,
      }
    },
    merchantColors(state): MerchantColors {
      return {
        background: state.merchant?.color?.background || '#000000',
        button: state.merchant?.color?.button || '#000000',
        text: state.merchant?.color?.text || '#ffffff',
      }
    },
  },
  actions: {
    async getMerchant() {
      const route = useRoute()

      try {
        const routeLocale = route?.query?.locale

        if (routeLocale) {
          this.locale = routeLocale.toString()
        } else {
          this.locale = 'fr'
        }

        setRootCss(this.gameContestColor)
      } catch (err) {
        return err
      }
    },
    async getGameContest(id: string, storeSlug: string) {
      try {
        if (!id || !storeSlug) {
          Sentry.captureMessage('Missing required parameters for getGameContest', {
            extra: { id, storeSlug },
          })

          return null
        }

        const res = await clientApi.client.contestGames.read(id, storeSlug)
        const { data } = res

        if (!data) {
          Sentry.captureMessage('No data returned from contestGames.read', {
            extra: { id, storeSlug },
          })

          return null
        }

        if (data.canLose) {
          const loseOutcome = data.outcomes.find((o: Outcomes) => o.isWinning === false)
          if (loseOutcome) {
            loseOutcome.contestGameRewardId = 1
          }
        }

        this.gameContest = data
        this.merchant = data.merchant
        this.isClickRequiredToPlay = data.isClickRequiredToPlay

        const getStoreIndex = data.stores.findIndex((s) => s.store.slug === storeSlug)
        if (getStoreIndex === -1) {
          Sentry.captureMessage('Store not found in contest data', {
            extra: { storeSlug, availableStores: data.stores.map((s) => s.store.slug) },
          })
        }
        this.redirectUrl = data.stores[getStoreIndex]?.trackedTargetUrl || ''

        setRootCss(this.gameContestColor)

        return data
      } catch (err) {
        Sentry.captureException(err, {
          extra: {
            id,
            storeSlug,
            message: 'Error in getGameContest',
          },
        })

        return Promise.reject(err)
      }
    },
    setContestReward(reward: ContestGameReward | null) {
      this.contestReward = reward
    },
  },
})
